import React, {useCallback, useState} from "react";
import PhoneInput from "../../../../../components/base/input/phone-input";
import Button from "../../../../../components/base/button/button";
import {Card} from "@mui/material";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../../../../components/form';
import ChangeCurrentOperatorPasswordModal from "../../../../../components/dialogs/change-current-operator-password";
import {useOperatorInformation, useOperatorInformationController} from "../../../../../hooks/use-operator-information";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import Grid2 from "@mui/material/Unstable_Grid2";


const EditOperatorProfileInformationSection = () => {
    const operatorInformation = useOperatorInformation();
    const {setCachedOperatorInformation} = useOperatorInformationController();
    const [changePasswordDialogState, setChangePasswordDialogState] = useState({open: false});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => Yup.object().shape({
        firstName: Yup.string().trim().nullable().required(ValidateMessages.required),
        lastName: Yup.string().trim().nullable().required(ValidateMessages.required),
        email: Yup.string().trim().email(ValidateMessages.incorrect).nullable().required(ValidateMessages.required),
        phone: Yup.string().trim().nullable()
            .required(ValidateMessages.required)
            .phoneNo(ValidateMessages.incorrectType("phone"), 10),
    }), []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = React.useMemo(() => ({
        firstName: operatorInformation?.firstName ?? '',
        lastName: operatorInformation?.lastName ?? '',
        email: operatorInformation?.email ?? '',
        phone: operatorInformation?.phone ?? '',
    }), [operatorInformation]);

    /**
     * Handles submission of the profile information changes:
     * Calls the api regarding this action
     * if the api call was not successful, shows an error dialog, otherwise, sets the current information to the caching service
     * @param values
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const response = await Api.updateOperator({...values, HasProfileChanged: false, ProfileImageFormat: "jpg"});
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            setCachedOperatorInformation(prevState => ({
                ...prevState,
                firstName: values.name,
                lastname: values.lastName,
                email: values.email,
                phone: values.phone,
            }));
        }
    }, [isMounted, setCachedOperatorInformation]);

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3}}>
                {
                    !operatorInformation &&
                    <div className={'loading current-operator-profile'}>
                        <div/>
                    </div>
                }
                {
                    !!operatorInformation &&
                    <Form
                        formProps={{
                            className: '100%',
                        }}
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                    >
                        {({pristine, values, form}) => (
                            <Grid2 container spacing={3}>
                                <Grid2 xs={12}>
                                    <p className={'text-lg font-weight-semi-bold text-color-1'}>
                                        My Profile Information
                                    </p>
                                </Grid2>

                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Firstname'}
                                        name={'firstName'}
                                        required={required.firstName}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Lastname'}
                                        name={'lastName'}
                                        required={required.lastName}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Email'}
                                        name={'email'}
                                        type={'email'}
                                        required={required.email}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <PhoneInput
                                        disabled={submitting}
                                        form={form}
                                        label={'Phone'}
                                        aria-label={'Phone Number of the operator'}
                                        name={'phone'}
                                        required={required.phone}
                                        value={values.phone ?? ''}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sx={{marginBlockStart: 2}}>
                                    <Button
                                        disabled={!!submitting || pristine}
                                        type={'submit'}
                                        variant={'contained'}
                                        color={'primary'}
                                        size={'medium'}
                                        sx={{minWidth: 200, marginInline: 'auto', display: 'flex'}}
                                    >
                                        {submitting ? 'Submitting...' : 'Save'}
                                    </Button>
                                </Grid2>
                                <Grid2 xs={12} sx={{marginBlockStart: -2}}>
                                    <Button
                                        onClick={() => setChangePasswordDialogState({open: true})}
                                        variant={'text'}
                                        color={'primary'}
                                        className={'w-100'}
                                    >
                                        Change Password
                                    </Button>
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                }
            </Card>
            <ChangeCurrentOperatorPasswordModal
                open={changePasswordDialogState.open}
                setOpen={() => setChangePasswordDialogState({open: false})}
            />
        </>
    );
}

export default EditOperatorProfileInformationSection;
